/**
 * @module modules/triage-panel-sk/triage-bucket-sk
 * @description <h2><code>triage-bucket-sk</code></h2>
 *
 * Represents a single custom triage bucket card. Implements a smart state machine (NEW -> APPLIED -> DIRTY)
 * to handle initial triage actions, display status badges, and track pending changes for re-application.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "TriageBucketSk", {
    enumerable: true,
    get: function() {
        return TriageBucketSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _newbugdialogsk = require("../new-bug-dialog-sk/new-bug-dialog-sk");
const _existingbugdialogsk = require("../existing-bug-dialog-sk/existing-bug-dialog-sk");
const _triageapi = require("../anomalies-table-sk/triage-api");
const _jsonOrThrow = require("../../../infra-sk/modules/jsonOrThrow");
const _bucketscontroller = require("./buckets-controller");
require("../../../elements-sk/modules/icons/clear-icon-sk");
require("../../../elements-sk/modules/icons/delete-icon-sk");
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let TriageBucketSk = class TriageBucketSk extends _lit.LitElement {
    createRenderRoot() {
        return this;
    }
    emitBucketUpdated(updatedBucket) {
        this.dispatchEvent(new CustomEvent('bucket-updated', {
            detail: {
                bucket: updatedBucket
            },
            bubbles: true,
            composed: true
        }));
    }
    render() {
        const b = this.bucket;
        return (0, _lit.html)`
      <div class="bucket-card">
        <div class="bucket-header">
          <div class="title-and-controls">
            <h3>${b.name} (${b.anomalies.length})</h3>
            ${b.anomalies.length > 0 ? (0, _lit.html)`
                  <button
                    class="icon-btn"
                    title="Clean (Empty staged anomalies)"
                    @click=${this.onClean}>
                    <clear-icon-sk></clear-icon-sk>
                  </button>
                ` : (0, _lit.html)`
                  <button class="icon-btn" title="Delete bucket entirely" @click=${this.onDelete}>
                    <delete-icon-sk></delete-icon-sk>
                  </button>
                `}
          </div>

          <div class="actions-group">
            ${b.triageState === 'NEW' ? (0, _lit.html)`
                  <button
                    class="primary ignore-btn"
                    ?disabled=${b.anomalies.length === 0}
                    @click=${this.onIgnore}>
                    Ignore
                  </button>
                  <button
                    class="primary new-bug-btn"
                    ?disabled=${b.anomalies.length === 0}
                    @click=${this.onNewBug}>
                    New Bug
                  </button>
                  <button
                    class="primary existing-bug-btn"
                    ?disabled=${b.anomalies.length === 0}
                    @click=${this.onExistingBug}>
                    Existing Bug
                  </button>
                ` : (0, _lit.html)`
                  <span class="status-badge">
                    ${b.actionType === 'IGNORE' ? 'Status: Ignored' : (0, _lit.html)`Bug
                          <a href="https://issues.chromium.org/issues/${b.bugId}" target="_blank"
                            >#${b.bugId}</a
                          >`}
                  </span>
                  <button
                    class="primary apply-btn"
                    ?disabled=${b.triageState !== 'DIRTY'}
                    @click=${this.onApply}>
                    Apply ${b.triageState === 'DIRTY' ? '(Pending)' : ''}
                  </button>
                `}
          </div>
        </div>

        <textarea
          class="bucket-textarea"
          placeholder="No anomalies staged. Click [${b.name}] on table rows, or paste anomaly IDs here."
          .value=${b.anomalies.map((a)=>`[${a.start_revision}-${a.end_revision}]:${a.test_path}:${a.id}`).join('\n')}
          @input=${this.onTextareaInput}>
        </textarea>

        <new-bug-dialog-sk
          .anomalies=${b.anomalies}
          .traceNames=${this.traceNames}
          @anomaly-changed=${this.onDialogAnomalyChanged}>
        </new-bug-dialog-sk>
        <existing-bug-dialog-sk
          .anomalies=${b.anomalies}
          .traceNames=${this.traceNames}
          @anomaly-changed=${this.onDialogAnomalyChanged}>
        </existing-bug-dialog-sk>
      </div>
    `;
    }
    onClean() {
        const updated = (0, _bucketscontroller.updateBucketDirtyState)(_object_spread_props(_object_spread({}, this.bucket), {
            anomalies: []
        }));
        this.emitBucketUpdated(updated);
    }
    onDelete() {
        this.dispatchEvent(new CustomEvent('bucket-deleted', {
            detail: {
                name: this.bucket.name
            },
            bubbles: true,
            composed: true
        }));
    }
    onTextareaInput(e) {
        const text = e.target.value;
        const pastedIds = text.split(/[\n,]+/).map((s)=>s.trim().split(':').pop()).filter(Boolean);
        const matching = this.anomalies.filter((a)=>pastedIds.includes(a.id));
        const updated = (0, _bucketscontroller.updateBucketDirtyState)(_object_spread_props(_object_spread({}, this.bucket), {
            anomalies: matching
        }));
        this.emitBucketUpdated(updated);
    }
    async onIgnore() {
        try {
            await (0, _triageapi.makeEditAnomalyRequest)(this.bucket.anomalies, this.traceNames, 'IGNORE');
            const updated = _object_spread_props(_object_spread({}, this.bucket), {
                actionType: 'IGNORE',
                triageState: 'APPLIED',
                lastAppliedIds: this.bucket.anomalies.map((a)=>a.id)
            });
            this.emitBucketUpdated(updated);
            this.fireAnomalyChanged('IGNORE');
        } catch (_e) {
        // errorMessage handled by makeEditAnomalyRequest
        }
    }
    onNewBug() {
        this.newBugDialog.setAnomalies(this.bucket.anomalies, this.traceNames);
        this.newBugDialog.fileNewBug();
    }
    onExistingBug() {
        this.existingBugDialog.setAnomalies(this.bucket.anomalies, this.traceNames);
        this.existingBugDialog.fetch_associated_bugs();
        this.existingBugDialog.open();
    }
    onDialogAnomalyChanged(e) {
        const bugId = e.detail.bugId;
        if (bugId !== undefined) {
            const updated = _object_spread_props(_object_spread({}, this.bucket), {
                actionType: 'EXISTING_BUG',
                bugId: bugId,
                triageState: 'APPLIED',
                lastAppliedIds: this.bucket.anomalies.map((a)=>a.id)
            });
            this.emitBucketUpdated(updated);
        }
    }
    async onApply() {
        const lastApplied = new Set(this.bucket.lastAppliedIds || []);
        const unapplied = this.bucket.anomalies.filter((a)=>!lastApplied.has(a.id));
        if (unapplied.length === 0) return;
        try {
            if (this.bucket.actionType === 'IGNORE') {
                await (0, _triageapi.makeEditAnomalyRequest)(unapplied, this.traceNames, 'IGNORE');
                this.fireAnomalyChanged('IGNORE');
            } else if (this.bucket.actionType === 'EXISTING_BUG' && this.bucket.bugId) {
                await this.associateAlerts(this.bucket.bugId, unapplied, this.traceNames);
                this.fireAnomalyChanged('ASSOCIATE');
            }
            const updated = _object_spread_props(_object_spread({}, this.bucket), {
                triageState: 'APPLIED',
                lastAppliedIds: this.bucket.anomalies.map((a)=>a.id)
            });
            this.emitBucketUpdated(updated);
        } catch (_e) {
        // errorMessage handled by utility
        }
    }
    async associateAlerts(bugId, anomalies, traceNames) {
        const requestBody = {
            bug_id: bugId,
            keys: anomalies.map((a)=>a.id),
            trace_names: traceNames
        };
        const res = await fetch('/_/triage/associate_alerts', {
            method: 'POST',
            body: JSON.stringify(requestBody),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        await (0, _jsonOrThrow.jsonOrThrow)(res);
        anomalies.forEach((a)=>a.bug_id = bugId);
    }
    fireAnomalyChanged(action) {
        this.dispatchEvent(new CustomEvent('anomaly-changed', {
            bubbles: true,
            composed: true,
            detail: {
                traceNames: this.traceNames,
                editAction: action,
                anomalies: this.bucket.anomalies
            }
        }));
    }
    constructor(...args){
        super(...args);
        this.anomalies = [];
        this.traceNames = [];
    }
};
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", typeof _bucketscontroller.TriageBucket === "undefined" ? Object : _bucketscontroller.TriageBucket)
], TriageBucketSk.prototype, "bucket", void 0);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Array)
], TriageBucketSk.prototype, "anomalies", void 0);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Array)
], TriageBucketSk.prototype, "traceNames", void 0);
_ts_decorate([
    (0, _decorators.query)('new-bug-dialog-sk'),
    _ts_metadata("design:type", typeof _newbugdialogsk.NewBugDialogSk === "undefined" ? Object : _newbugdialogsk.NewBugDialogSk)
], TriageBucketSk.prototype, "newBugDialog", void 0);
_ts_decorate([
    (0, _decorators.query)('existing-bug-dialog-sk'),
    _ts_metadata("design:type", typeof _existingbugdialogsk.ExistingBugDialogSk === "undefined" ? Object : _existingbugdialogsk.ExistingBugDialogSk)
], TriageBucketSk.prototype, "existingBugDialog", void 0);
TriageBucketSk = _ts_decorate([
    (0, _decorators.customElement)('triage-bucket-sk')
], TriageBucketSk);
